<template>
  <div class="p-6 space-y-4 pb-32">
    <LoadingIndicator v-if="loading" message="Loading page..." />
    <p v-else-if="error" class="text-sm text-red-700">{{ error }}</p>
    <template v-else-if="page">
      <div class="flex flex-wrap items-center gap-3">
        <h1 class="text-lg font-semibold text-gray-900" :class="{ sensitive: page.id }" data-spoof-kind="label">{{ page.title }}</h1>
        <span v-if="page.origin === 'shared' && page.owner_name" class="text-xs text-gray-500">Shared by <span class="sensitive" data-spoof-kind="person">{{ page.owner_name }}</span></span>
        <span v-else class="text-xs text-gray-500">{{ originLabel }}</span>
        <div class="ml-auto flex flex-wrap items-center gap-2">
          <FolderPicker v-if="canMove" :model-value="page.stem || ''" :stems="stems" :disabled="busy" @update:model-value="moveTo" />
          <button v-if="canCustomize" type="button" :class="BTN" :disabled="busy" @click="customize">Customize</button>
          <router-link v-if="canHermes" :to="{ name: 'Agent', query: { profile: 'pages' } }" :class="BTN">Open Hermes</router-link>
          <router-link v-if="canEditInAdmin" :to="{ name: 'AdminAgentPageEdit', params: { id: page.id } }" :class="BTN">Edit</router-link>
          <button v-if="isMine" type="button" :class="BTN" :disabled="busy" @click="share">
            {{ page.shared ? 'Stop sharing' : 'Share with organisation' }}
          </button>
          <div v-if="isMine && page.shared" class="relative">
            <button type="button" :class="BTN" :disabled="busy" @click="toggleEntityMenu">
              <font-awesome-icon :icon="['fas', pageEntityIds.length ? 'building' : 'globe']" :class="['mr-1.5', pageEntityIds.length ? 'text-amber-600' : 'text-primary-600']" />
              {{ pageEntityIds.length ? `Entities (${pageEntityIds.length})` : 'All entities' }}
            </button>
            <div v-if="showPracticePicker" class="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 w-[340px] p-2.5 space-y-2 text-xs">
              <div class="flex items-center justify-between gap-2 pb-2 border-b border-gray-100">
                <span class="text-gray-500">Portal tab order (smaller = earlier)</span>
                <input :value="pagePortalOrder ?? ''" type="number" placeholder="Auto" class="border border-gray-200 rounded px-2 py-1 text-xs w-20 text-right" @change="setPortalOrder($event.target.value)">
              </div>
              <div class="text-gray-500 flex items-center justify-between">
                <span>Visible to selected entities &amp; their children</span>
                <button v-if="pageEntityIds.length" type="button" class="text-blue-600 hover:underline" @click="setPageEntities([])">All</button>
              </div>
              <div v-if="selectedEntityRows.length" class="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                <span v-for="e in selectedEntityRows" :key="e.id" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
                  <span class="sensitive">{{ e.name }}</span>
                  <span v-if="e.type" class="text-[10px] text-gray-400">({{ e.type }})</span>
                  <button type="button" class="text-gray-400 hover:text-gray-700" @click="togglePageEntity(e)">×</button>
                </span>
              </div>
              <div class="flex gap-1.5">
                <select v-model="pickerTypeId" class="border border-gray-200 rounded px-2 py-1 text-xs bg-white flex-1" @change="searchPickerEntities">
                  <option :value="null">Entity type…</option>
                  <option v-for="t in entityTypes" :key="t.id" :value="t.id">{{ t.type_name }}</option>
                </select>
                <input v-model="pickerSearch" type="text" placeholder="Filter by name…" class="sensitive border border-gray-200 rounded px-2 py-1 text-xs flex-1" @input="searchPickerEntities">
              </div>
              <div v-if="pickerLoading" class="py-3 text-center text-gray-400">Loading…</div>
              <div v-else-if="pickerResults.length" class="max-h-48 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded">
                <label v-for="row in pickerResults" :key="row.id" class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" :checked="pageEntityIds.includes(Number(row.id))" :disabled="busy" @change="togglePageEntity({ id: Number(row.id), name: row.name, type: row.entity_type_name })">
                  <span class="text-gray-800 sensitive truncate flex-1">{{ row.name }}</span>
                  <span class="text-[10px] text-gray-400">{{ row.entity_type_name }}</span>
                </label>
              </div>
              <div v-else-if="pickerTypeId" class="py-3 text-center text-gray-400">No matching entities.</div>
            </div>
            <div v-if="showPracticePicker" class="fixed inset-0 z-10" @click="showPracticePicker = false"></div>
          </div>
          <button v-if="isMine && page.subject" type="button" :class="BTN" :disabled="busy" @click="reset">Reset to default</button>
          <ExportMenu :exports="exports" size="sm" />
        </div>
      </div>
      <div v-if="entityId && page.chrome !== false" class="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2 text-sm">
        <button type="button" class="text-blue-600 hover:underline" @click="setEntity(null)">All Entities</button>
        <span class="text-gray-400">/</span>
        <span class="text-gray-900 font-medium sensitive">{{ entityName || `Entity ${entityId}` }}</span>
        <div v-if="parents.length" class="relative ml-2">
          <button type="button" class="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200" @click="showParents = !showParents">Parents...</button>
          <div v-if="showParents" class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[240px]">
            <button
              v-for="parent in parents" :key="parent.id" type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
              @click="setEntity(parent.id); showParents = false"
            >
              <span class="text-gray-800 sensitive">{{ parent.name }}</span>
              <span class="text-xs text-gray-400 ml-4">{{ parent.entity_type_name }}</span>
            </button>
          </div>
          <div v-if="showParents" class="fixed inset-0 z-10" @click="showParents = false"></div>
        </div>
        <router-link :to="{ name: 'Entity', params: { entity_id: entityId } }" class="ml-auto text-accent hover:underline whitespace-nowrap">View Entity ↗</router-link>
      </div>
      <p v-if="actionError" class="text-sm text-red-700">{{ actionError }}</p>
      <div ref="exportable" class="bg-white border border-gray-200 rounded-lg p-3">
        <PageCanvas
          v-if="spec"
          ref="canvas"
          :page-id="page.id"
          :subject="page.id ? null : { type: page.subject, id: page.subject_id }"
          :spec="spec"
          :widgets="page.widgets || {}"
          :host="host"
          @drill-entity="drill"
          @select-patient="openPatient"
        />
        <AgentHtmlCard v-else-if="page.html" :html="page.html" :sources="page.sources || []" :page-id="page.id" />
      </div>
    </template>

    <div v-if="selectedPatientId" class="fixed inset-0 z-50 flex justify-end" @click.self="selectedPatientId = null">
      <div class="bg-black/25 absolute inset-0" @click="selectedPatientId = null"></div>
      <div class="relative w-[720px] h-full bg-white shadow-xl overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <span class="font-medium text-sm text-gray-900">Patient Details</span>
          <button type="button" class="text-gray-400 hover:text-gray-600" @click="selectedPatientId = null">&times;</button>
        </div>
        <Patient_Drawer :patient-identifier="selectedPatientId" :entity-id="entityId" :sidebar-mode="false" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import FolderPicker from './FolderPicker.vue';
import { allStems, domainStems } from '@/utils/pageFolders';
import { exportElementPdf, exportElementPng, fileSlug } from '@/utils/workflowExports';
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import ExportMenu from '../widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '../widgetcomponents/library/LoadingIndicator.vue';
import PageCanvas from '../Beta/widgets/PageCanvas.vue';
import AgentHtmlCard from '../Beta/AgentHtmlCard.vue';

const BTN = 'px-3 py-1.5 text-xs font-medium text-gray-800 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50';
const ORIGINS = {
  'built-in': 'Built-in page', configured: 'Your organisation\'s layout', mine: 'Your customised page', shared: 'Shared page',
};

export default {
  name: 'PageHost',
  components: { Patient_Drawer, ExportMenu, LoadingIndicator, PageCanvas, AgentHtmlCard, FolderPicker },
  inject: { refreshMine: { default: null } },
  props: {
    subject: { type: String, required: true },
    subjectId: { type: Number, default: null },
  },
  data() {
    return {
      page: null, loading: true, error: '', actionError: '', busy: false, entityName: '', parents: [], showParents: false,
      selectedPatientId: null, years: [], exporting: '', stems: [], showPracticePicker: false,
      entityTypes: [], pickerTypeId: null, pickerSearch: '', pickerResults: [], pickerLoading: false, BTN,
    };
  },
  computed: {
    pageEntityIds() {
      const raw = (this.page && (this.page.entity_ids || (this.page.spec && this.page.spec.entity_ids))) || [];
      return Array.isArray(raw) ? raw.map(Number) : [];
    },
    pagePortalOrder() {
      const v = this.page && (this.page.portal_order ?? (this.page.spec && this.page.spec.portal_order));
      return v === null || v === undefined || v === '' ? null : Number(v);
    },
    selectedEntityRows() {
      const known = (this.page && this.page.entities) || [];
      const byId = Object.fromEntries(known.map((e) => [Number(e.id), e]));
      return this.pageEntityIds.map((id) => byId[id] || { id, name: `Entity ${id}`, type: '' });
    },
    isMine() {
      return !!this.page && this.page.origin === 'mine';
    },
    originLabel() {
      if (!this.page) return '';
      if (this.page.origin === 'shared') return `Shared by ${this.page.owner_name || 'a colleague'}`;
      return ORIGINS[this.page.origin] || '';
    },
    canAuthor() {
      return canAccessFeature(FEATURES.MODULE_AGENT);
    },
    canCustomize() {
      return !!this.page && !!this.page.subject && this.page.origin !== 'mine' && this.canAuthor;
    },
    canHermes() {
      return this.isMine && this.canAuthor;
    },
    canMove() {
      return !!this.page && this.subject === 'mine' && this.page.origin === 'mine' && this.canAuthor;
    },
    canEditInAdmin() {
      return this.isMine && canAccessFeature(FEATURES.MODULE_ADMIN);
    },
    entityId() {
      const raw = this.$route.query.entity;
      return /^\d+$/.test(String(raw || '')) ? Number(raw) : null;
    },
    host() {
      return { ...((this.page && this.page.implicit) || {}), entity: this.entityId };
    },
    spec() {
      const spec = this.page && this.page.spec;
      if (!spec || !(spec.placements || []).length) return null;
      const year = (spec.filters || []).find((f) => f.name === 'year');
      if (!year || !this.years.length || this.years.includes(Number(year.default))) return spec;
      return { ...spec, filters: spec.filters.map((f) => (f.name === 'year' ? { ...f, default: Math.max(...this.years) } : f)) };
    },
    exports() {
      return [
        { type: 'pdf', loading: this.exporting === 'pdf', action: () => this.runExport('pdf') },
        { type: 'chart', menuLabel: 'Image (PNG)', loading: this.exporting === 'png', action: () => this.runExport('png') },
        { type: 'excel', menuLabel: 'Data (Excel)', loading: this.exporting === 'xlsx', action: () => this.runExport('xlsx') },
      ];
    },
  },
  watch: {
    entityId: { immediate: true, handler: 'loadEntity' },
  },
  mounted() {
    this.load();
    this.loadYears();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        if (this.subject === 'mine') {
          const { data } = await axios.get(`/api/impact_analytics/agent-pages/${this.subjectId}/`);
          if (data.subject_type) {
            this.$router.replace({ path: data.subject_type === 'overall' ? '/pages' : `/pages/${data.subject_type}/${data.subject_id}`, query: this.$route.query });
            return;
          }
          this.page = { ...data, origin: data.can_edit ? 'mine' : 'shared', subject: '', implicit: {}, chrome: true };
          if (this.canMove) this.loadStems();
        } else {
          const { data } = await axios.get('/api/impact_analytics/agent-pages/resolve/', { params: { subject: this.subject, id: this.subjectId } });
          this.page = data;
        }
        this.markPage();
      } catch (err) {
        this.error = err?.response?.data?.error || 'That page could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    async loadStems() {
      const [{ data }, domains] = await Promise.all([axios.get('/api/impact_analytics/agent-pages/'), domainStems()]);
      this.stems = [...new Set([...domains, ...allStems(data.filter((page) => !page.subject_type))])];
    },
    moveTo(value) {
      return this.act(async () => {
        const { data } = await axios.post(`/api/impact_analytics/agent-pages/${this.page.id}/move/`, { stem: value });
        this.page = { ...this.page, stem: data.stem };
        if (this.refreshMine) await this.refreshMine();
        await this.loadStems();
      });
    },
    markPage() {
      const query = { ...this.$route.query };
      if (this.page && this.page.id && this.subject !== 'mine') query.page = String(this.page.id);
      else delete query.page;
      if (query.page !== this.$route.query.page) this.$router.replace({ path: this.$route.path, query });
    },
    async loadYears() {
      const { data } = await axios.post('api/impact_analytics/observation-dates/', {});
      this.years = Object.keys(data.dates_by_year || {}).map(Number);
    },
    async loadEntity() {
      this.entityName = '';
      this.parents = [];
      if (!this.entityId) return;
      const [entity, parents] = await Promise.all([
        axios.get(`api/entities/${this.entityId}/`),
        axios.get(`api/entities/${this.entityId}/parents/`),
      ]);
      this.entityName = entity.data.name;
      this.parents = parents.data;
    },
    setEntity(id) {
      const query = { ...this.$route.query };
      if (id) query.entity = String(id);
      else delete query.entity;
      this.$router.push({ path: this.$route.path, query });
    },
    drill(row) {
      if (row && row.entity_id) this.setEntity(row.entity_id);
    },
    openPatient({ bene_mbi_id }) {
      this.selectedPatientId = bene_mbi_id;
    },
    async act(run) {
      this.busy = true;
      this.actionError = '';
      try {
        await run();
      } catch (err) {
        this.actionError = err?.response?.data?.error || 'That did not work.';
      } finally {
        this.busy = false;
      }
    },
    customize() {
      return this.act(async () => {
        await axios.post('/api/impact_analytics/agent-pages/customize/', { subject: this.page.subject, id: this.page.subject_id });
        await this.load();
      });
    },
    reset() {
      if (!window.confirm('Delete your customised page and go back to the default?')) return null;
      return this.act(async () => {
        await axios.delete(`/api/impact_analytics/agent-pages/${this.page.id}/`);
        await this.load();
      });
    },
    share() {
      return this.act(async () => {
        const { data } = await axios.post(`/api/impact_analytics/agent-pages/${this.page.id}/share/`, { shared: !this.page.shared });
        this.page = { ...this.page, shared: data.shared, entity_ids: data.entity_ids || [], entities: data.entities || this.page.entities || [] };
      });
    },
    async toggleEntityMenu() {
      this.showPracticePicker = !this.showPracticePicker;
      if (!this.showPracticePicker || this.entityTypes.length) return;
      try {
        const { data } = await axios.get('api/entity-types/');
        this.entityTypes = data || [];
        const practice = this.entityTypes.find((t) => t.type_name === 'Practice') || this.entityTypes[0];
        if (practice) {
          this.pickerTypeId = practice.id;
          await this.searchPickerEntities();
        }
      } catch {
        this.entityTypes = [];
      }
    },
    async searchPickerEntities() {
      if (!this.pickerTypeId) {
        this.pickerResults = [];
        return;
      }
      this.pickerLoading = true;
      try {
        const params = { entity_type_id: this.pickerTypeId };
        if (this.pickerSearch.trim()) params.name = this.pickerSearch.trim();
        const { data } = await axios.get('api/entities/', { params });
        this.pickerResults = data.results || data || [];
      } catch {
        this.pickerResults = [];
      } finally {
        this.pickerLoading = false;
      }
    },
    setPageEntities(ids) {
      return this.act(async () => {
        const { data } = await axios.post(`/api/impact_analytics/agent-pages/${this.page.id}/share/`, { entity_ids: ids });
        const spec = this.page.spec ? { ...this.page.spec, entity_ids: data.entity_ids || [] } : this.page.spec;
        this.page = { ...this.page, entity_ids: data.entity_ids || [], entities: data.entities || [], spec };
      });
    },
    setPortalOrder(raw) {
      const value = String(raw ?? '').trim() === '' ? '' : Number(raw);
      return this.act(async () => {
        const { data } = await axios.post(`/api/impact_analytics/agent-pages/${this.page.id}/share/`, { portal_order: value });
        const spec = this.page.spec ? { ...this.page.spec, portal_order: data.portal_order } : this.page.spec;
        this.page = { ...this.page, portal_order: data.portal_order, spec };
      });
    },
    togglePageEntity(entity) {
      const id = Number(typeof entity === 'object' ? entity.id : entity);
      const next = this.pageEntityIds.includes(id)
        ? this.pageEntityIds.filter((x) => x !== id)
        : [...this.pageEntityIds, id];
      return this.setPageEntities(next);
    },
    async runExport(kind) {
      this.exporting = kind;
      try {
        const name = `${fileSlug(this.page.title)}.${kind}`;
        if (kind === 'xlsx') {
          if (!this.$refs.canvas) { this.actionError = 'This page has no table data to export.'; return; }
          const sheets = await this.$refs.canvas.exportRows();
          if (!sheets.length) { this.actionError = 'This page has no table data to export.'; return; }
          await exportToExcel(sheets.map((x) => x.rows), name, { sheetNames: sheets.map((x, i) => (x.name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 28) || `Sheet ${i + 1}`)) });
        } else if (kind === 'pdf') await exportElementPdf(this.$refs.exportable, name);
        else await exportElementPng(this.$refs.exportable, name);
      } finally {
        this.exporting = '';
      }
    },
  },
};
</script>
